// oxlint-disable react/jsx-pascal-case
import { type Component, type ComponentProps, type JSX, Show } from "solid-js";
import { cn } from "@cashsync/shared";

interface EmptyStateProps {
  icon: Component<ComponentProps<"svg">>;
  title: string;
  description?: string;
  class?: string;
  /**
   * Optional actionable CTA (first run): a button/link that lets the user
   * leave the empty state without wondering where to start.
   */
  action?: JSX.Element;
  /**
   * When provided, the illustrative icon becomes an interactive target that
   * triggers the same action as the primary CTA (open a dialog, navigate, or
   * focus an input) so the empty state never feels like a dead end. Tapping the
   * icon is a discoverable shortcut to the CTA — leave it undefined for purely
   * informational empty states (the icon stays decorative).
   */
  onIconActivate?: () => void;
  /**
   * Accessible label for the interactive icon (required when `onIconActivate`
   * is set). Should describe the action, e.g. "Create your first account".
   */
  iconActionLabel?: string;
}

/**
 * Empty state with a theme-aware decorative SVG halo behind the icon. The SVG
 * is inline and uses `currentColor` + tokens (no raster assets), so it adapts
 * to every theme and stays crisp. Purely decorative → `aria-hidden`.
 */
export const EmptyState: Component<EmptyStateProps> = (props) => (
  <div class={cn("flex flex-1 flex-col items-center justify-center p-8 text-center", props.class)}>
    <div class="relative mb-6 animate-scale-in">
      {/* Decorative concentric halo */}
      <svg
        class="absolute inset-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 text-primary/10"
        viewBox="0 0 120 120"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="60" cy="60" r="58" stroke="currentColor" stroke-width="1" />
        <circle cx="60" cy="60" r="44" stroke="currentColor" stroke-width="1" />
        <circle cx="60" cy="60" r="30" stroke="currentColor" stroke-width="1" />
      </svg>
      {/* When an action is wired, the icon becomes a real button so tap/click/
          keyboard (Enter/Space, native to <button>) triggers the CTA. Otherwise
          it stays a decorative div. Focus ring is visible; the float animation
          is already suppressed under the global prefers-reduced-motion guard. */}
      <Show
        when={props.onIconActivate}
        fallback={
          <div class="relative rounded-full bg-primary/5 p-6 ring-1 ring-primary/10 animate-float">
            <props.icon class="h-12 w-12 text-primary/60" />
          </div>
        }
      >
        {(activate) => (
          <button
            type="button"
            aria-label={props.iconActionLabel ?? props.title}
            onClick={() => {
              activate()();
            }}
            class="group relative rounded-full bg-primary/5 p-6 ring-1 ring-primary/10 animate-float transition-[transform,box-shadow,background-color] duration-200 hover:bg-primary/10 hover:ring-primary/25 hover:shadow-lg hover:shadow-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
          >
            <props.icon class="h-12 w-12 text-primary/60 transition-colors group-hover:text-primary" />
          </button>
        )}
      </Show>
    </div>
    <h3 class="text-lg font-semibold tracking-tight">{props.title}</h3>
    <Show when={props.description}>
      <p class="mt-2 text-sm text-muted-foreground max-w-xs mx-auto">{props.description}</p>
    </Show>
    <Show when={props.action}>
      <div class="mt-5">{props.action}</div>
    </Show>
  </div>
);
